import type { ChatMessage, ChatParams } from "@/types";
import { getActiveProvider } from "../providers";
import { createTrailingThrottle } from "../streaming/streamThrottle";
import { AIServiceError } from "../types";

/**
 * chatService
 *
 * Pure orchestration over the active `AIProvider` — no store or UI
 * dependencies, so it's trivially unit-testable against a mocked provider.
 * Store wiring (turning deltas into message updates, persisting, generating
 * titles) lives one layer up, in `useChat` (`src/hooks/useChat.ts`).
 */
const controllers = new Map<string, AbortController>();

const FLUSH_INTERVAL_MS = 40;

export function isGenerating(conversationId: string): boolean {
  return controllers.has(conversationId);
}

/** Aborts the in-flight generation for a conversation, if any. */
export function stop(conversationId: string): void {
  controllers.get(conversationId)?.abort();
}

export interface StreamAssistantReplyRequest {
  baseUrl: string;
  model: string;
  messages: ChatMessage[];
  params: ChatParams;
}

export interface StreamAssistantReplyCallbacks {
  /** Called with the full accumulated content so far, throttled to avoid a
   * re-render per token. */
  onDelta: (content: string) => void;
  /** Called exactly once — whether the stream finished naturally or was
   * stopped by the user. `stopped` distinguishes the two. */
  onDone: (content: string, stopped: boolean) => void;
  onError: (error: AIServiceError) => void;
}

/** Streams one assistant reply for `conversationId`. Call `stop` with the
 * same id from anywhere else to cancel it mid-flight. */
export async function streamAssistantReply(
  conversationId: string,
  request: StreamAssistantReplyRequest,
  callbacks: StreamAssistantReplyCallbacks,
): Promise<void> {
  const controller = new AbortController();
  controllers.set(conversationId, controller);

  let full = "";
  const throttle = createTrailingThrottle(FLUSH_INTERVAL_MS, () => callbacks.onDelta(full));

  try {
    const provider = getActiveProvider();
    for await (const delta of provider.streamChat({ ...request, signal: controller.signal })) {
      full += delta.content;
      throttle.trigger();
    }
    throttle.flushNow();
    callbacks.onDone(full, false);
  } catch (err) {
    throttle.flushNow();
    const error = err instanceof AIServiceError ? err : new AIServiceError("unknown", String(err));
    if (error.kind === "aborted") {
      callbacks.onDone(full, true);
    } else {
      callbacks.onError(error);
    }
  } finally {
    controllers.delete(conversationId);
  }
}

/** Best-effort short title for a new conversation; `null` on failure so
 * callers can fall back to a heuristic (e.g. truncating the first message). */
export async function generateTitle(
  baseUrl: string,
  model: string,
  seedText: string,
): Promise<string | null> {
  return getActiveProvider().generateTitle(baseUrl, model, seedText);
}
